import { useEffect, useState } from 'react';
import {
  apiAddProductImage,
  apiGetProduct,
  apiRemoveProductImage,
  apiSetProductOptions,
  apiUpdateProduct,
  type ProductDetail,
  type ProductPatch,
  type ProductStatus,
  type Shop,
  type Variant,
} from '~/lib/api/shop';
import { classNames } from '~/utils/classNames';
import {
  BTN_GHOST,
  BTN_SECONDARY,
  CARD,
  ErrorBanner,
  Field,
  LoadError,
  Pill,
  Section,
  Select,
  SkeletonCard,
  Spinner,
} from '~/components/workbench/tools/payments/ui';
import { useResource } from '~/components/workbench/tools/payments/use-resource';
import { OptionsFields } from './OptionsFields';
import {
  apiErrorMessage,
  isHttpUrl,
  optionDraftsFrom,
  parseOptionDrafts,
  parseTags,
  parseTaxRate,
  PRODUCT_STATUSES,
  productStatusMeta,
  taxRateInput,
  type OptionDraft,
} from './shop-model';
import { VariantsTable } from './VariantsTable';
import { AdminActionsMenu, AdminExtensionSlot } from '~/components/workbench/admin-extensions';

export function ProductDetailView({
  appId,
  shop,
  productId,
  onBack,
}: {
  appId: string;
  shop: Shop;
  productId: string;
  onBack: () => void;
}) {
  const detail = useResource(() => apiGetProduct(appId, productId), `${appId}:${productId}`);
  const data = detail.data;

  // Only called once the detail has loaded.
  const patchDetail = (fn: (d: ProductDetail) => ProductDetail) => detail.setData((prev) => fn(prev!));

  const onVariantChanged = (next: Variant) =>
    patchDetail((d) => ({ ...d, variants: d.variants.map((v) => (v.id === next.id ? next : v)) }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-2">
        <button type="button" onClick={onBack} className={BTN_GHOST}>
          <div className="i-ph:arrow-left text-[13px]" />
          All products
        </button>
        <div className="flex items-center gap-1">
          <AdminActionsMenu appId={appId} target="shop.product.actions" resourceId={productId} compact />
          {data && (
            <Pill
              tone={productStatusMeta(data.product.status).tone}
              label={productStatusMeta(data.product.status).label}
            />
          )}
        </div>
      </div>

      {data ? (
        <>
          <ProductFields appId={appId} detail={data} onSaved={(product) => patchDetail((d) => ({ ...d, product }))} />
          <OptionsEditor appId={appId} shop={shop} detail={data} onSaved={(next) => detail.setData(next)} />
          <ImagesEditor appId={appId} detail={data} onChanged={() => void detail.reload()} />
          <VariantsTable
            appId={appId}
            variants={data.variants}
            currency={shop.currency}
            skuPattern={shop.skuPattern}
            onVariantChanged={onVariantChanged}
          />
          <AdminExtensionSlot appId={appId} target="shop.product.details" resourceId={productId} />
        </>
      ) : detail.error ? (
        <LoadError message="Could not load this product." onRetry={() => void detail.reload()} />
      ) : (
        <>
          <SkeletonCard lines={4} />
          <SkeletonCard lines={3} />
        </>
      )}
    </div>
  );
}

function ProductFields({
  appId,
  detail,
  onSaved,
}: {
  appId: string;
  detail: ProductDetail;
  onSaved: (product: ProductDetail['product']) => void;
}) {
  const p = detail.product;
  const [title, setTitle] = useState(p.title);
  const [description, setDescription] = useState(p.description ?? '');
  const [status, setStatus] = useState<ProductStatus>(p.status);
  const [tags, setTags] = useState(p.tags.join(', '));
  const [productType, setProductType] = useState(p.productType ?? '');
  const [taxRate, setTaxRate] = useState(taxRateInput(p.taxRateBps));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setTitle(p.title);
    setDescription(p.description ?? '');
    setStatus(p.status);
    setTags(p.tags.join(', '));
    setProductType(p.productType ?? '');
    setTaxRate(taxRateInput(p.taxRateBps));
  }, [p.id, p.updatedAt]);

  const patch: ProductPatch = {};

  if (title.trim() !== p.title) {
    patch.title = title.trim();
  }

  if ((description.trim() || null) !== p.description) {
    patch.description = description.trim() || null;
  }

  if (status !== p.status) {
    patch.status = status;
  }

  if (JSON.stringify(parseTags(tags)) !== JSON.stringify(p.tags)) {
    patch.tags = parseTags(tags);
  }

  if ((productType.trim() || null) !== p.productType) {
    patch.productType = productType.trim() || null;
  }

  const taxRateBps = parseTaxRate(taxRate);

  if (taxRateBps !== undefined && taxRateBps !== p.taxRateBps) {
    patch.taxRateBps = taxRateBps;
  }

  const dirty = Object.keys(patch).length > 0;

  const save = async (e: React.FormEvent) => {
    e.preventDefault();

    if (taxRateBps === undefined) {
      setError('Tax rate must be a percentage from 0 to 100, like 18 or 12.5.');
      return;
    }

    if (!dirty) {
      return;
    }

    if (!title.trim()) {
      setError('A product needs a title.');
      return;
    }

    setSaving(true);
    setError(null);
    setSaved(false);

    try {
      onSaved(await apiUpdateProduct(appId, p.id, patch));
      setSaved(true);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save this product.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Section title="Product" icon="i-ph:package">
      <form onSubmit={save} className={classNames(CARD, 'space-y-3 p-4')}>
        <Field label="Title">
          <input value={title} onChange={(e) => setTitle(e.target.value)} className="ui-input" />
        </Field>
        <div className="flex gap-2">
          <div className="w-32">
            <Field label="Status">
              <Select value={status} onChange={(v) => setStatus(v as ProductStatus)} label="Status">
                {PRODUCT_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {productStatusMeta(s).label}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <div className="min-w-0 flex-1">
            <Field label="Product type">
              <input
                value={productType}
                onChange={(e) => setProductType(e.target.value)}
                placeholder="Mug"
                className="ui-input !py-2"
              />
            </Field>
          </div>
          <div className="w-28">
            <Field label="Tax rate (%)">
              <input
                value={taxRate}
                onChange={(e) => setTaxRate(e.target.value)}
                placeholder="None"
                inputMode="decimal"
                aria-invalid={taxRateBps === undefined}
                className={classNames('ui-input-mono !py-2', taxRateBps === undefined && '!border-danger')}
              />
            </Field>
          </div>
        </div>
        <p className="-mt-1 text-[11px] leading-relaxed text-muted">
          Only Active products show in the storefront. <span className="font-geist-mono">/{p.slug}</span>
        </p>
        <Field label="Description">
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="ui-input resize-y"
          />
        </Field>
        <Field label="Tags" hint="Comma separated.">
          <input value={tags} onChange={(e) => setTags(e.target.value)} className="ui-input" />
        </Field>

        {error && <ErrorBanner message={error} />}

        <div className="flex items-center gap-3">
          <button type="submit" disabled={saving || (!dirty && taxRateBps !== undefined)} className={BTN_SECONDARY}>
            {saving && <Spinner />}
            {saving ? 'Saving…' : 'Save product'}
          </button>
          {saved && !dirty && <span className="text-[11px] text-muted">Saved</span>}
        </div>
      </form>
    </Section>
  );
}

function OptionsEditor({
  appId,
  shop,
  detail,
  onSaved,
}: {
  appId: string;
  shop: Shop;
  detail: ProductDetail;
  onSaved: (next: ProductDetail) => void;
}) {
  const initial = optionDraftsFrom(detail.optionSets);
  const [drafts, setDrafts] = useState<OptionDraft[]>(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const parsed = parseOptionDrafts(drafts);
  const current = parseOptionDrafts(initial);
  const dirty = parsed.ok && current.ok ? JSON.stringify(parsed.options) !== JSON.stringify(current.options) : true;

  const save = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!parsed.ok) {
      setError(parsed.error);
      return;
    }

    setSaving(true);
    setError(null);
    setResult(null);

    try {
      const res = await apiSetProductOptions(appId, detail.product.id, { options: parsed.options });
      const { created, archived, ...rest } = res;

      // The PUT response omits archived variants; refetch so the archived list stays complete.
      onSaved(await apiGetProduct(appId, detail.product.id).catch(() => rest));
      setDrafts(optionDraftsFrom(rest.optionSets));
      setResult(
        [
          created.length ? `${created.length} new variant${created.length === 1 ? '' : 's'}` : '',
          archived.length ? `${archived.length} archived` : '',
        ]
          .filter(Boolean)
          .join(', ') || 'Options saved',
      );
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save the options.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Section title="Options" icon="i-ph:sliders">
      <form onSubmit={save} className={classNames(CARD, 'space-y-3 p-4')}>
        <OptionsFields
          drafts={drafts}
          onChange={(next) => {
            setDrafts(next);
            setResult(null);
          }}
          productTitle={detail.product.title}
          skuPattern={shop.skuPattern}
          disabled={saving}
        />
        <p className="text-[11px] leading-relaxed text-muted">
          Saving rebuilds the variants. Combinations you remove are archived, never deleted, so past orders and stock
          history stay intact. New combinations get fresh SKUs and start untracked.
        </p>
        {error && <ErrorBanner message={error} />}
        <div className="flex items-center gap-3">
          <button type="submit" disabled={saving || !dirty || !parsed.ok} className={BTN_SECONDARY}>
            {saving && <Spinner />}
            {saving ? 'Saving…' : 'Save options'}
          </button>
          {result && <span className="text-[11px] text-muted">{result}</span>}
        </div>
      </form>
    </Section>
  );
}

function ImagesEditor({ appId, detail, onChanged }: { appId: string; detail: ProductDetail; onChanged: () => void }) {
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!isHttpUrl(url)) {
      setError('Paste an image link starting with https://');
      return;
    }

    setBusy('add');

    try {
      await apiAddProductImage(appId, detail.product.id, { url: url.trim(), alt: detail.product.title });
      setUrl('');
      onChanged();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not add this image.'));
    } finally {
      setBusy(null);
    }
  };

  const remove = async (imageId: string) => {
    setBusy(imageId);
    setError(null);

    try {
      await apiRemoveProductImage(appId, detail.product.id, imageId);
      onChanged();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not remove this image.'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Section title="Images" icon="i-ph:image">
      <div className={classNames(CARD, 'space-y-3 p-4')}>
        {detail.images.length > 0 ? (
          <div className="grid grid-cols-4 gap-2">
            {detail.images.map((img) => (
              <div
                key={img.id}
                className="group relative aspect-square overflow-hidden rounded-lg border border-hairline bg-sunken"
              >
                <img src={img.url} alt={img.alt ?? ''} className="h-full w-full object-cover" loading="lazy" />
                <button
                  type="button"
                  onClick={() => void remove(img.id)}
                  disabled={busy !== null}
                  className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-md bg-black/60 text-primary opacity-0 transition-opacity focus:opacity-100 group-hover:opacity-100 touch:opacity-100"
                  aria-label="Remove image"
                  title="Remove image"
                >
                  {busy === img.id ? <Spinner className="text-[11px]" /> : <div className="i-ph:trash text-[12px]" />}
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[12px] text-muted">No images yet. The first image is the product's cover.</p>
        )}

        <form onSubmit={add} className="flex items-center gap-2">
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://…/mug.jpg"
            aria-label="Image URL"
            spellCheck={false}
            className="ui-input-mono min-w-0 flex-1 !py-1.5 !text-[12px]"
          />
          <button type="submit" disabled={busy !== null || !url.trim()} className={BTN_SECONDARY}>
            {busy === 'add' && <Spinner />}
            Add image
          </button>
        </form>

        {error && <ErrorBanner message={error} />}
      </div>
    </Section>
  );
}
